/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Stack } from '@fluentui/react/lib/Stack';
import orderBy from 'lodash.orderby';
import { observer } from 'mobx-react';
import React from 'react';
import { DeletePromptModal } from 'src/core/components/DeletePromptModal';
import { TablePagination } from 'src/core/components/TablePagination';
import { SortKeyType } from 'src/core/components/TableProps';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { usePagination } from 'src/core/hooks/usePagination';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { useSelection } from 'src/core/hooks/useSelection';
import { useUpdateState } from 'src/core/hooks/useUpdateState';
import { getEventLogger } from 'src/core/services/telemetry/logEventBuilder';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { noop } from 'src/core/utils/base';
import { substringSearchStrategy } from 'src/core/utils/searchStrategies/substringSearchStrategy';
import { createKeyedStringNormalizer } from 'src/core/utils/strings';
import { patternsPageMessages } from 'src/luis/appShell/buildPane/patternsPage/patternsPageMessages';
import { Intent } from 'src/luis/modules/intents/Intent';
import { Pattern } from 'src/luis/modules/patterns/Pattern';
import { usePatterns } from 'src/luis/modules/patterns/usePatterns';
import { ModelType } from 'src/luis/types/ModelType';
import { EntityStore } from 'src/luis/types/stores/entities/EntityStore';
import { CreateEntityModal } from 'src/luis/verticals/createEntityModal/CreateEntityModal';
import { PatternTable } from 'src/luis/verticals/patternsTable/components/PatternTable';
import { PatternTableCommandBar } from 'src/luis/verticals/patternsTable/components/PatternTableCommandBar';
import { FiltersItem } from 'src/verticals/utterancesFilters/types/Filters';

export type PatternTableContainerProps = {
    /**
     * Filters applied to the displayed patterns.
     */
    appliedFilters: FiltersItem[];

    /**
     * If patterns can be filtered and their intents can be re-assigned.
     */
    areAdvancedOptionsEnabled: boolean;

    /**
     * A string representing the activity source.
     */
    loggerActivitySource: string;

    /**
     * A callback on opening filters option.
     */
    onFilterPattern?: () => void;

    /**
     * A callback for re-assigning a pattern's intent.
     */
    onReassignIntent?: (selectedItems: Pattern[], intent: Intent) => void;

    /**
     * A callback to set the patterns count upon any filtering changes.
     */
    setFilteredPatternsLength?: (length: number) => void;

    /**
     * A callback to initialize filters after patterns are loaded.
     */
    initializeFilters?: () => void;
};

export const PatternTableContainer = observer((props: PatternTableContainerProps) => {
    const {
        appliedFilters,
        loggerActivitySource,
        areAdvancedOptionsEnabled,
        setFilteredPatternsLength = noop,
        initializeFilters = noop,
        onFilterPattern = noop,
        onReassignIntent = noop
    } = props;

    const { localize } = useLocalization();

    const [query, setQuery] = React.useState('');
    const { selection, selectedItems } = useSelection<Pattern>();
    const [patternIdBeingEdited, setPatternIdBeingEdited] = React.useState<React.ReactText>('');

    const [modalsState, setModalsState] = useUpdateState(defaultModalStates);
    const [{ entityName, roleName }, setEntityRoleNames] = React.useState<{ entityName: string; roleName: string }>({
        entityName: '',
        roleName: ''
    });

    const { dispatcher, rootStore } = useGlobalState();
    const { patterns, isLoading: isPatternLoading, entities, intents } = usePatterns();
    const [sortKey, setSortKey] = React.useState<SortKeyType<Pattern>>({ key: 'text', order: 'asc' });

    const { userRole, displayedUserRole } = rootStore.serviceKey;
    const { canWrite, canDeleteData } = mapUserRoleToUserActions(userRole);

    const filteredPatterns = orderBy(
        substringSearchStrategy(patterns, query, 'text'),
        [createKeyedStringNormalizer(sortKey.key)],
        [sortKey.order]
    ).filter(
        pattern =>
            // Checks if there is applied filters.
            // Checks if the labeled intent of the pattern is one of the selected filter intents.
            // Checks if the pattern has one of the selected filter entities.
            appliedFilters.length === 0 ||
            appliedFilters.find(filter => filter.id === pattern.labeledIntent.id) ||
            pattern.usedEntitiesAndRoles.some(entity => appliedFilters.find(filter => filter.id === entity.id))
    );

    const eventLoggerRef = React.useRef(getEventLogger(loggerActivitySource));

    const { currentPage, setCurrentPage, pageItems, pageCount } = usePagination(filteredPatterns);

    React.useEffect(() => setFilteredPatternsLength(filteredPatterns.length), [filteredPatterns.length]);

    React.useEffect(() => {
        initializeFilters();
    }, [isPatternLoading]);

    const defaultTracker = useProgressTracker({
        onComplete: () => {
            setModalsState(defaultModalStates);
            selection.setAllSelected(false);
        }
    });

    /**
     *  Callback for when after the entity is created
     *
     * @param entity The entity created.
     */
    const onEntityCreated = (entity: EntityStore) => {
        setModalsState({ isEntityCreationModalHidden: true });

        if (roleName) {
            onAddNewRole(entity.name, roleName);
        }
        eventLoggerRef.current.end('CreateEntityDialog');
    };

    /**
     * Updates existing pattern with the given pattern properties.
     *
     * @param pattern The new pattern properties to be updated.
     */
    const handleUpdatePattern = (pattern: Pattern) => {
        dispatcher.dispatch('updatePatterns', {
            patterns: [
                {
                    id: pattern.id,
                    text: pattern.text,
                    intentId: pattern.labeledIntent.id as string
                }
            ],
            tracker: defaultTracker,
            toastData: {
                running: { title: localize(patternsPageMessages.updatePatternToastStart) },
                completed: { title: localize(patternsPageMessages.updatePatternToastSuccess) },
                failed: { title: localize(patternsPageMessages.updatePatternToastFailed) }
            }
        });
    };

    /**
     * Deletes existing patterns.
     *
     * @param selectedPatterns The patterns to be deleted.
     */
    const handleDeletePatterns = (selectedPatterns: Pattern[]) => {
        dispatcher.dispatch('deletePatterns', {
            patternsIds: selectedPatterns.map(pattern => pattern.id),
            tracker: defaultTracker,
            toastData: {
                running: { title: localize(patternsPageMessages.deletePatternToastStart) },
                completed: { title: localize(patternsPageMessages.deletePatternToastSuccess) },
                failed: { title: localize(patternsPageMessages.deletePatternToastFailed) }
            }
        });
    };

    /**
     * Callback function to handle the create new role action.
     */
    const onAddNewRole = (roleEntityName: string, rName: string) => {
        const entityId = rootStore.currentLuisAppStore.currentAppVersionStore.entityStoreHouse.findByName(roleEntityName).id;
        dispatcher.dispatch('addEntityRole', { entityId, roleName: rName });
    };

    /**
     * Callback function to handle the create new entity action.
     */
    const onCreateNewEntity = (eName: string) => {
        setEntityRoleNames({ entityName: eName, roleName: null });
        setModalsState({ isEntityCreationModalHidden: false });
        eventLoggerRef.current.start('CreateEntityDialog');
    };

    /**
     * Callback function to handle the create new entity and role action.
     */
    const onCreateNewEntityAndRole = (eName: string, rName: string) => {
        setEntityRoleNames({ entityName: eName, roleName: rName });
        setModalsState({ isEntityCreationModalHidden: false });
        eventLoggerRef.current.start('CreateEntityDialog');
    };

    /*
     * Closes entity creation modal and logEvent for the modal dismissal.
     *
     */
    const dismissCreateEntityModal = () => {
        setModalsState({ isEntityCreationModalHidden: true });
        eventLoggerRef.current.end('CreateEntityDialog');
        eventLoggerRef.current.log('CreateEntityDialogDismissed');
    };

    /**
     * logEvent for searching.
     * @param searchQuery
     */
    const onQueryChange = (searchQuery: string) => {
        eventLoggerRef.current.log('PatternTableSearchTriggered');
        setQuery(searchQuery);
    };

    return (
        <Stack tokens={{ childrenGap: 10 }}>
            <PatternTableCommandBar
                intents={intents}
                selectedItems={selectedItems}
                query={query}
                onQueryChange={onQueryChange}
                onReassignIntent={intent => onReassignIntent(selectedItems, intent)}
                onDeletePattern={() => setModalsState({ isDeleteModalHidden: false })}
                onFilterPattern={onFilterPattern}
                areAdvancedOptionsEnabled={areAdvancedOptionsEnabled}
                onEditPattern={() => setPatternIdBeingEdited(selectedItems[0].id)}
                userRole={userRole}
                displayedUserRole={displayedUserRole}
            />

            <PatternTable
                patternIdBeingEdited={patternIdBeingEdited}
                onEdit={val => setPatternIdBeingEdited(val ? selectedItems[0].id : null)}
                areAdvancedOptionsEnabled={areAdvancedOptionsEnabled && canWrite}
                sortKey={sortKey}
                isLoading={isPatternLoading}
                items={pageItems}
                selection={selection}
                intents={intents}
                entities={entities}
                areItemsFiltered={Boolean(query || appliedFilters.length)}
                overallItemsCount={filteredPatterns.length}
                onUpdatePattern={handleUpdatePattern}
                onDeletePattern={pattern => handleDeletePatterns([pattern])}
                onDeleteKeyUp={() => canDeleteData && setModalsState({ isDeleteModalHidden: false })}
                onCreateNewEntity={onCreateNewEntity}
                onCreateNewRole={onAddNewRole}
                onCreateNewEntityAndRole={onCreateNewEntityAndRole}
                onSortColumn={c => {
                    setSortKey({ key: c.key as keyof Pattern, order: c.isSortedDescending ? 'asc' : 'desc' });
                    selection.setAllSelected(false);
                }}
                canPatternBeEditedOrDeleted={canWrite}
            />

            <TablePagination pageCount={pageCount} currentPage={currentPage} onPageChange={p => setCurrentPage(p.selected)} />

            <DeletePromptModal
                hidden={modalsState.isDeleteModalHidden}
                onDelete={() => handleDeletePatterns(selectedItems)}
                dialogContentProps={{
                    title: localize(patternsPageMessages.deletePatternsModalTitle),
                    subText: localize(patternsPageMessages.deletePatternsModalDescription)
                }}
                onDismiss={() => setModalsState({ isDeleteModalHidden: true })}
            />

            <CreateEntityModal
                entityName={entityName}
                allowedModelTypes={[ModelType.Simple, ModelType.List, ModelType.Regex, ModelType.PatternAny]}
                hidden={modalsState.isEntityCreationModalHidden}
                onSubmit={onEntityCreated}
                onDismiss={dismissCreateEntityModal}
            />
        </Stack>
    );
});

const defaultModalStates = { isDeleteModalHidden: true, isEntityCreationModalHidden: true };
